import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { ArrowLeft, CircleCheck, CircleX, Info } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { firestore } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { User } from '../../../../shared/types/user.types';
import { Vehicle, IWarranty } from '../../../../shared/types/vehicle.types';
import { VehicleTransfer, VehicleTransferStatus } from '../../../../shared/types/vehicle-transfer.types';
import useVehicleTransfer from '../../wrappers/use-vehicle-transfer';
import useVehicle from '../../wrappers/use-vehicle';
import DocumentList from '../../components/DocumentList';
import { useTranslation } from 'react-i18next';
import useDateFormat from '../../utils/useDateFormat';
import {
    infoRowStyles,
    transferDetailStyles,
} from './TransferDetail.styles';

const statusConfig: Record<VehicleTransferStatus, { color: 'warning' | 'success' | 'error' | 'info' | 'default' }> = {
    pending: { color: 'warning' },
    approved: { color: 'success' },
    rejected: { color: 'error' },
    infoRequested: { color: 'info' },
};

const InfoRow: React.FC<{ label: string; value: React.ReactNode; mono?: boolean }> = ({ label, value, mono }) => (
    <Box sx={infoRowStyles.root}>
        <Typography variant="body2" color="text.secondary" fontWeight={500}>{label}</Typography>
        {typeof value === 'string'
            ? <Typography variant="body2" fontFamily={mono ? 'monospace' : undefined}>{value}</Typography>
            : value}
    </Box>
);

const TransferDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const formatDate = useDateFormat();
    const { user } = useAuth();

    const warrantyTypeLabel = (type: string) => {
        if (type === 'standart') return t('adminTransferDetail.warrantyStandard');
        if (type === 'extended') return t('adminTransferDetail.warrantyExtended');
        return type;
    };
    const { get: getTransfer, edit: editTransfer } = useVehicleTransfer();
    const { get: getVehicle } = useVehicle();
    const [transfer, setTransfer] = useState<VehicleTransfer | null>(null);
    const [vehicle, setVehicle] = useState<Vehicle | null>(null);
    const [fromUser, setFromUser] = useState<User | null>(null);
    const [toUser, setToUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [note, setNote] = useState('');
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [newWarranty, setNewWarranty] = useState<Partial<IWarranty>>({});

    useEffect(() => {
        if (!id) return;
        getTransfer(id).then(async (data) => {
            if (!data) { setNotFound(true); setLoading(false); return; }
            setTransfer(data);
            const [v, fu, tu] = await Promise.all([
                getVehicle(data.vehicleUid),
                data.fromUserUid ? getDoc(doc(firestore, 'user', data.fromUserUid)) : null,
                data.toUserUid   ? getDoc(doc(firestore, 'user', data.toUserUid))   : null,
            ]);
            if (v) {
                setVehicle(v);
                if (v.warranty) setNewWarranty(v.warranty);
            }
            if (fu?.exists()) setFromUser({ uid: fu.id, ...fu.data() } as User);
            if (tu?.exists()) setToUser({ uid: tu.id, ...tu.data() } as User);

            setLoading(false);
        });
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const isPending = transfer?.status === 'pending' || transfer?.status === 'infoRequested' || !transfer?.status;
    const canApprove = !!newWarranty.type && !!newWarranty.validUntil;

    const handleWarrantyTypeChange = (type: string) => {
        const years = type === 'extended' ? 5 : 2;
        const base = new Date(vehicle?.purchaseDate ?? new Date());
        base.setFullYear(base.getFullYear() + years);
        const validUntil = base.toISOString().split('T')[0];
        setNewWarranty({ type, validUntil });
    };

    const handleApproveConfirmed = async () => {
        if (!id) return;
        await editTransfer(id, {
            status: 'approved',
            statusNote: '',
            warranty: newWarranty as IWarranty,
            approvedAt: new Date(),
            approvedBy: user!.uid,
        });
        setShowApproveModal(false);
        navigate('/app/admin/transfers');
    };

    const handleReject = async () => {
        if (!id) return;
        await editTransfer(id, { status: 'rejected', statusNote: note });
        setShowRejectModal(false);
        navigate('/app/admin/transfers');
    };

    const handleRequestInfo = async () => {
        if (!id) return;
        await editTransfer(id, { status: 'infoRequested', statusNote: note });
        setShowInfoModal(false);
        navigate('/app/admin/transfers');
    };

    if (loading) {
        return (
            <Box sx={transferDetailStyles.loadingContainer}>
                <CircularProgress />
            </Box>
        );
    }

    if (notFound || !transfer) {
        return <Alert severity="error">{t('adminTransferDetail.notFound')}</Alert>;
    }

    const status = statusConfig[transfer.status ?? 'pending'];

    return (
        <Box sx={transferDetailStyles.root}>
            <Box sx={transferDetailStyles.header}>
                <Box sx={transferDetailStyles.headerLeft}>
                    <IconButton component={Link} to="/app/admin/transfers" size="small">
                        <ArrowLeft size={20} />
                    </IconButton>
                    <Typography variant="h4" fontWeight={700}>{t('adminTransferDetail.title')}</Typography>
                    <Chip label={t(`vehicleTransferStatus.${transfer.status ?? 'pending'}`)} color={status.color as 'warning' | 'success' | 'error' | 'info' | 'default'} size="small" />
                </Box>
                {isPending && (
                    <Box sx={transferDetailStyles.headerActions}>
                        {transfer?.status === 'pending' && (
                            <Button variant="outlined" startIcon={<Info size={20} />} onClick={() => setShowInfoModal(true)}>{t('adminTransferDetail.requestInfo')}</Button>
                        )}
                        <Button variant="contained" color="error" startIcon={<CircleX size={20} />} onClick={() => setShowRejectModal(true)}>{t('adminTransferDetail.reject')}</Button>
                        <Button variant="contained" color="success" startIcon={<CircleCheck size={20} />} onClick={() => setShowApproveModal(true)} disabled={!canApprove}>{t('adminTransferDetail.approve')}</Button>
                    </Box>
                )}
            </Box>

            {/* Stroj */}
            <Paper elevation={0} sx={transferDetailStyles.card}>
                <Box sx={transferDetailStyles.cardHeader}>
                    <Typography variant="subtitle1" fontWeight={600}>{t('adminTransferDetail.machineTitle')}</Typography>
                </Box>
                <InfoRow label={t('adminTransferDetail.vinLabel')} value={transfer.vehicleUid} mono />
                <Divider />
                <InfoRow label={t('adminTransferDetail.machineTypeLabel')} value={vehicle?.model ?? '—'} />
                <Divider />

                <InfoRow
                    label={t('adminTransferDetail.warrantyTypeLabel')}
                    value={isPending ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                            <FormControl size="small" error={!newWarranty.type} sx={{ minWidth: 200 }}>
                                <Select value={newWarranty.type ?? ''} displayEmpty onChange={(e) => handleWarrantyTypeChange(e.target.value)}>
                                    <MenuItem value="" disabled><em>{t('adminTransferDetail.warrantySelectPlaceholder')}</em></MenuItem>
                                    <MenuItem value="standart">{t('adminTransferDetail.warrantyStandard')}</MenuItem>
                                    <MenuItem value="extended">{t('adminTransferDetail.warrantyExtended')}</MenuItem>
                                </Select>
                            </FormControl>
                            {newWarranty.type && newWarranty.type !== vehicle?.warranty?.type && vehicle?.warranty?.type && (
                                <Typography variant="body2" color="text.secondary">{t('adminTransferDetail.warrantyOriginal', { value: warrantyTypeLabel(vehicle.warranty.type) })}</Typography>
                            )}
                        </Box>
                    ) : (transfer.warranty?.type ? warrantyTypeLabel(transfer.warranty.type) : (vehicle?.warranty?.type ? warrantyTypeLabel(vehicle.warranty.type) : '—'))}
                />
                <Divider />
                <InfoRow
                    label={t('adminTransferDetail.warrantyUntilLabel')}
                    value={isPending ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                            <TextField
                                size="small"
                                type="date"
                                value={newWarranty.validUntil ?? ''}
                                onChange={(e) => setNewWarranty((w) => ({ ...w, validUntil: e.target.value }))}
                                error={!newWarranty.validUntil}
                                InputLabelProps={{ shrink: true }}
                            />
                            {newWarranty.validUntil && newWarranty.validUntil !== vehicle?.warranty?.validUntil && vehicle?.warranty?.validUntil && (
                                <Typography variant="body2" color="text.secondary">{t('adminTransferDetail.warrantyOriginal', { value: formatDate(vehicle.warranty.validUntil) })}</Typography>
                            )}
                        </Box>
                    ) : (transfer.warranty?.validUntil ? formatDate(transfer.warranty.validUntil) : (vehicle?.warranty?.validUntil ? formatDate(vehicle.warranty.validUntil) : '—'))}
                />
            </Paper>

            {/* Majitelé */}
            <Box sx={transferDetailStyles.ownersGrid}>
                {/* Původní majitel */}
                <Paper elevation={0} sx={transferDetailStyles.card}>
                    <Box sx={transferDetailStyles.cardHeader}>
                        <Typography variant="subtitle1" fontWeight={600}>{t('adminTransferDetail.fromOwnerTitle')}</Typography>
                    </Box>
                    <InfoRow label={t('adminTransferDetail.nameLabel')} value={fromUser?.name ?? '—'} />
                    <Divider />
                    <InfoRow label={t('adminTransferDetail.emailLabel')} value={fromUser?.email ?? '—'} />
                    <Divider />
                    <InfoRow label={t('adminTransferDetail.phoneLabel')} value={fromUser?.phone ?? '—'} />
                </Paper>

                {/* Nový majitel */}
                <Paper elevation={0} sx={transferDetailStyles.card}>
                    <Box sx={transferDetailStyles.cardHeader}>
                        <Typography variant="subtitle1" fontWeight={600}>{t('adminTransferDetail.toOwnerTitle')}</Typography>
                    </Box>
                    <InfoRow label={t('adminTransferDetail.nameLabel')} value={transfer.toUserDisplayName ?? toUser?.name ?? '—'} />
                    <Divider />
                    <InfoRow label={t('adminTransferDetail.emailLabel')} value={toUser?.email ?? '—'} />
                    <Divider />
                    <InfoRow label={t('adminTransferDetail.phoneLabel')} value={toUser?.phone ?? '—'} />
                </Paper>
            </Box>

            {/* Žádost */}
            <Paper elevation={0} sx={transferDetailStyles.card}>
                <Box sx={transferDetailStyles.cardHeader}>
                    <Typography variant="subtitle1" fontWeight={600}>{t('adminTransferDetail.requestTitle')}</Typography>
                </Box>
                <InfoRow label={t('adminTransferDetail.requestDate')} value={formatDate(transfer.createdAt)} />
                {transfer.statusNote && (
                    <>
                        <Divider />
                        <InfoRow
                            label={transfer.status === 'rejected' ? t('adminTransferDetail.rejectionLabel') : t('adminTransferDetail.infoRequestedLabel')}
                            value={
                                <Typography variant="body2" color={transfer.status === 'rejected' ? 'error.main' : 'info.main'}>
                                    {transfer.statusNote}
                                </Typography>
                            }
                        />
                    </>
                )}
                <Divider />
                <InfoRow label={t('adminTransferDetail.documentsLabel')} value={<DocumentList filters={{ vehicleTransferUid: id }} uploadData={{ vehicleUid: transfer.vehicleUid, vehicleTransferUid: id }} />} />
            </Paper>

            {/* Dialog: Schválit */}
            <Dialog open={showApproveModal} onClose={() => setShowApproveModal(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{t('adminTransferDetail.approveDialogTitle')}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        {t('adminTransferDetail.approveDialogBody', { model: vehicle?.model ?? transfer.vehicleUid, vin: transfer.vehicleUid })}
                    </Typography>
                    <Box sx={transferDetailStyles.approveChangesBox}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 1, alignItems: 'start' }}>
                            <Typography variant="body2" color="text.secondary">{t('adminTransferDetail.approveDialogNewOwner')}</Typography>
                            <Box>
                                <Typography variant="body2" fontWeight={600}>{transfer.toUserDisplayName ?? toUser?.name ?? '—'}</Typography>
                                {toUser?.email && <Typography variant="body2" color="text.secondary">{toUser.email}</Typography>}
                            </Box>
                            <Typography variant="body2" color="text.secondary">{t('adminTransferDetail.approveDialogWarrantyType')}</Typography>
                            <Typography variant="body2" fontWeight={600}>{newWarranty.type ? warrantyTypeLabel(newWarranty.type) : '—'}</Typography>
                            <Typography variant="body2" color="text.secondary">{t('adminTransferDetail.approveDialogWarrantyUntil')}</Typography>
                            <Typography variant="body2" fontWeight={600}>
                                {newWarranty.validUntil ? formatDate(newWarranty.validUntil) : '—'}
                            </Typography>
                        </Box>
                    </Box>
                    <Typography variant="body2" mt={1.5}>{t('adminTransferDetail.approveDialogConfirm')}</Typography>
                </DialogContent>
                <DialogActions sx={transferDetailStyles.dialogActions}>
                    <Button onClick={() => setShowApproveModal(false)}>{t('common.cancel')}</Button>
                    <Button variant="contained" color="success" onClick={handleApproveConfirmed}>{t('adminTransferDetail.approveConfirmButton')}</Button>
                </DialogActions>
            </Dialog>

            {/* Dialog: Zamítnout */}
            <Dialog open={showRejectModal} onClose={() => setShowRejectModal(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{t('adminTransferDetail.rejectDialogTitle')}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" mb={2}>{t('adminTransferDetail.rejectDialogText')}</Typography>
                    <TextField label={t('adminTransferDetail.rejectReasonLabel')} multiline rows={4} fullWidth placeholder={t('adminTransferDetail.rejectReasonPlaceholder')} value={note} onChange={(e) => setNote(e.target.value)} />
                </DialogContent>
                <DialogActions sx={transferDetailStyles.dialogActions}>
                    <Button onClick={() => setShowRejectModal(false)}>{t('common.cancel')}</Button>
                    <Button variant="contained" color="error" onClick={handleReject}>{t('adminTransferDetail.rejectButton')}</Button>
                </DialogActions>
            </Dialog>

            {/* Dialog: Vyžádat doplnění */}
            <Dialog open={showInfoModal} onClose={() => setShowInfoModal(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{t('adminTransferDetail.infoDialogTitle')}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" mb={2}>{t('adminTransferDetail.infoDialogText')}</Typography>
                    <TextField label={t('adminTransferDetail.infoMessageLabel')} multiline rows={4} fullWidth placeholder={t('adminTransferDetail.infoMessagePlaceholder')} value={note} onChange={(e) => setNote(e.target.value)} />
                </DialogContent>
                <DialogActions sx={transferDetailStyles.dialogActions}>
                    <Button onClick={() => setShowInfoModal(false)}>{t('common.cancel')}</Button>
                    <Button variant="contained" onClick={handleRequestInfo}>{t('adminTransferDetail.infoSendButton')}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TransferDetail;
